import { createElement, useState } from "react";
import { ChevronRight, Eye, EyeOff, GripVertical, RefreshCw } from "lucide-react";

const SKIP = new Set(["SCRIPT", "STYLE", "LINK", "META", "BR"]);
let dragId = null;

function layerName(n, depth, index) {
  const custom = n.getAttribute("data-lp-name");
  if (custom) return custom;
  if (depth === 0) return "Dobra " + (index + 1);
  let s = n.tagName.toLowerCase();
  if (n.id) s += "#" + n.id;
  else if (typeof n.className === "string" && n.className) s += "." + n.className.split(" ")[0];
  return s;
}

const LayerNode = ({ n, depth, ed, index }) => {
  const [open, setOpen] = useState(depth < 1);
  const [over, setOver] = useState(null);
  const id = n.getAttribute("data-lp");
  const kids = [...n.children].filter((c) => !SKIP.has(c.tagName));
  const sel = ed.selected === id;
  const hidden = ed.getStyle(id, "display") === "none";
  const name = layerName(n, depth, index);
  const rename = () => { const v = window.prompt("Nome da camada", name); if (v !== null) { v ? n.setAttribute("data-lp-name", v) : n.removeAttribute("data-lp-name"); ed.commit(); } };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const src = ed.el(dragId); const side = over; setOver(null);
    if (!src || !side || src === n || src.contains(n) || !n.parentElement) return;
    side === "before" ? n.before(src) : n.after(src);
    ed.setSelected(dragId); ed.commit();
  };
  return (
    <li>
      <div
        data-testid={`layer-${id}`}
        draggable
        onDragStart={(e) => { dragId = id; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); }}
        onDragEnd={() => { dragId = null; setOver(null); }}
        onDragOver={(e) => { if (!dragId || dragId === id) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; const r = e.currentTarget.getBoundingClientRect(); setOver(e.clientY < r.top + r.height / 2 ? "before" : "after"); }}
        onDragLeave={() => setOver(null)}
        onDrop={onDrop}
        className={`flex items-center gap-1 rounded-md pr-1 text-xs transition-colors ${sel ? "bg-[var(--blue)] text-white" : "hover:bg-slate-100"} ${over === "before" ? "border-t-2 border-[var(--blue)]" : over === "after" ? "border-b-2 border-[var(--blue)]" : "border-y-2 border-transparent"}`}
        style={{ paddingLeft: depth * 12 + 4 }}
      >
        <GripVertical size={11} className="shrink-0 opacity-30 cursor-grab active:cursor-grabbing" />
        <button onClick={() => setOpen(!open)} className={`h-6 w-4 grid place-items-center ${kids.length ? "" : "opacity-0 pointer-events-none"}`}><ChevronRight size={12} className={`transition-transform ${open ? "rotate-90" : ""}`} /></button>
        <button onClick={() => { ed.setSelected(id); n.scrollIntoView({ block: "center", behavior: "smooth" }); }} onDoubleClick={rename} className={`flex-1 text-left py-1 truncate ${hidden ? "opacity-50 line-through" : ""}`}>{name}</button>
        <button data-testid={`layer-toggle-${id}`} title={hidden ? "Mostrar" : "Ocultar"} onClick={() => ed.setStyle(id, "display", hidden ? "" : "none")} className="h-6 w-6 grid place-items-center opacity-60 hover:opacity-100">{hidden ? <EyeOff size={12} /> : <Eye size={12} />}</button>
      </div>
      {open && kids.length > 0 && <ul>{kids.map((c, i) => createElement(LayerNode, { key: c.getAttribute("data-lp") || i, n: c, depth: depth + 1, ed, index: i }))}</ul>}
    </li>
  );
};

export const Layers = ({ ed, onClose }) => {
  const [, force] = useState(0);
  const d = ed.doc();
  const roots = d?.body ? [...d.body.children].filter((c) => !SKIP.has(c.tagName) && c.hasAttribute("data-lp")) : [];
  void ed.tick;
  return (
    <aside data-testid="layers-panel" className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col">
      <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between text-xs font-semibold"><span>Camadas</span><div className="flex gap-1"><button onClick={() => force((x) => x + 1)} title="Atualizar" className="h-6 w-6 grid place-items-center hover:bg-slate-100 rounded"><RefreshCw size={12} /></button><button onClick={onClose} className="h-6 px-2 hover:bg-slate-100 rounded">✕</button></div></div>
      <ul className="p-2 overflow-y-auto flex-1">{roots.map((r, i) => <LayerNode key={r.getAttribute("data-lp")} n={r} depth={0} ed={ed} index={i} />)}</ul>
      <p className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100">Arraste para reordenar. Duplo clique renomeia. Olho oculta no dispositivo atual.</p>
    </aside>
  );
};
